import { GraviButton, Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Switch } from 'antd'
import moment from 'moment'
import { type MutableRefObject, useMemo, useState } from 'react'
import type { GridApi } from 'ag-grid-community'
import type { Match, MatchOverviewResponse } from '../../../../api/useSupplierReconciliation/types/match'
import type { SupplierReconciliationMetadataResponse } from '../../../../api/useMetadata/types'
import { DownloadButton } from '../../../_SiteWide/Grid/ActionButtons/DownloadButton'
import { CreateInvoiceModal } from './CreateInvoiceModal/CreateInvoiceModal'
import { DateRangeFilter } from '../../../_SiteWide/DateRangeFilter'

interface DateFilters {
  From: moment.Moment
  To: moment.Moment
}

interface MatchViewToolbarProps {
  dateRange: [Date?, Date?]
  // DateRangeFilter props
  dateFilters: DateFilters
  setDateFilters: (filters: DateFilters) => void
  dayRangeStorage: { back: number; ahead: number }
  setDayRangeStorage: (range: { back: number; ahead: number }) => void
  assignedToMe: boolean
  setAssignedToMe: (assignedToMe: boolean) => void
  // Tab state for dynamic slider range
  isVoidedViewActive: boolean
  activeStatus: string | null
  // Default helpers for reset functionality
  getDefaultApprovedDayRangeStorage: () => { back: number; ahead: number }
  getDefaultVoidedDayRangeStorage: () => { back: number; ahead: number }
  getDefaultOtherTabsDayRangeStorage: () => { back: number; ahead: number }
  gridAPIRef: MutableRefObject<GridApi<Match>>
  handleCreateInvoice: (invoiceData: { body: unknown }) => Promise<void>
  metadata?: SupplierReconciliationMetadataResponse
  metadataLoading: boolean
  filteredData?: MatchOverviewResponse
}

export function MatchViewToolbar({
  dateRange,
  dateFilters,
  setDateFilters,
  dayRangeStorage,
  setDayRangeStorage,
  assignedToMe,
  setAssignedToMe,
  isVoidedViewActive,
  activeStatus,
  getDefaultApprovedDayRangeStorage,
  getDefaultVoidedDayRangeStorage,
  getDefaultOtherTabsDayRangeStorage,
  gridAPIRef,
  handleCreateInvoice,
  metadata,
  metadataLoading,
  filteredData,
}: MatchViewToolbarProps) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const getFileName = () => {
    return `MatchView_${dateRange[0]?.toISOString().split('T')[0]}_${dateRange[1]?.toISOString().split('T')[0]}.xlsx`
  }

  // Calculate dynamic slider range and default values based on current tab
  // Get current tab default for reset functionality
  const currentTabDefault = useMemo(() => {
    if (isVoidedViewActive) {
      return getDefaultVoidedDayRangeStorage()
    } else if (activeStatus === 'approved') {
      return getDefaultApprovedDayRangeStorage()
    }
    return getDefaultOtherTabsDayRangeStorage()
  }, [
    isVoidedViewActive,
    activeStatus,
    getDefaultVoidedDayRangeStorage,
    getDefaultApprovedDayRangeStorage,
    getDefaultOtherTabsDayRangeStorage,
  ])

  // Calculate slider-specific min/max values based on current tab
  // These only affect the slider, not the calendar date picker
  const sliderLimits = useMemo(() => {
    if (isVoidedViewActive) {
      // Voided view: allow -90 to +15 range for slider
      return { sliderMin: -90, sliderMax: 15 }
    } else if (activeStatus === 'approved') {
      // Approved tab: allow -15 to +15 range for slider
      return { sliderMin: -15, sliderMax: 15 }
    }
    // Other tabs (All Open, Unmatched, Hold, Unapproved): allow -45 to +15 range for slider
    return { sliderMin: -45, sliderMax: 15 }
  }, [isVoidedViewActive, activeStatus])

  // Function to get dates from storage - following OrderMovements pattern
  const getFromStorage = () => {
    if (dayRangeStorage) {
      return {
        From: moment().add(dayRangeStorage.back, 'days').startOf('day'),
        To: moment().add(dayRangeStorage.ahead, 'days').endOf('day'),
      }
    }
    return null
  }

  return (
    <>
      <Horizontal verticalCenter gap={8}>
        {/* Assigned to Me Toggle */}
        <Horizontal gap={8} style={{ flex: 0, overflow: 'visible' }}>
          <Texto style={{ whiteSpace: 'nowrap' }} appearance='default'>
            Assigned to Me
          </Texto>
          <Switch checked={assignedToMe} onChange={(checked) => setAssignedToMe(checked)} />
        </Horizontal>

        {/* Export Button */}
        <DownloadButton fileName={getFileName()} gridAPIRef={gridAPIRef} />

        {/* Date Range Filter */}
        <DateRangeFilter
          currentFilters={dateFilters}
          setCurrentFilters={setDateFilters as (filters: Record<string, any>) => void}
          getFromStorage={getFromStorage}
          dayRangeStorage={dayRangeStorage}
          setDayRangeStorage={setDayRangeStorage}
          defaultDayRangeStorage={currentTabDefault}
          sliderMin={sliderLimits.sliderMin}
          sliderMax={sliderLimits.sliderMax}
        />

        {/* Create Invoice Button */}
        <GraviButton theme2 onClick={() => setIsCreateModalOpen(true)} icon={<MaterialIcon icon='add' />}>
          Create Invoice
        </GraviButton>
      </Horizontal>

      <CreateInvoiceModal
        open={isCreateModalOpen}
        onCancel={() => setIsCreateModalOpen(false)}
        metadata={metadata}
        metadataLoading={metadataLoading}
        handleCreateInvoice={handleCreateInvoice}
        onSuccess={() => {
          setIsCreateModalOpen(false)
        }}
      />
    </>
  )
}
